/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useCallback, useState, useContext } from 'react'
import { useServiceContext } from '../../../root'
import { folderTypesApiService } from '../../../services/api/api.foldertypes'
import type { FolderType } from '../../../services/api/api.foldertypes'
import { RcmTableDataWrapper } from '../../../components/rcm-table/RcmTableDataWrapper'
import { RcmTableDataContext } from '../../../components/rcm-table/RcmTableDataContext'
import RcmTableFilterTrigger from '../../../components/rcm-table/RcmTableFilterTrigger'
import Loader from '../../../components/shared/Loader'
import PageMenu from './PageMenu'
import TableFilter from './TableFilter'
import Table from './Table'
import ModalEdit from './Actions/ModalEdit'

const FolderTypesContent: React.FC = () => {
  const tableContext = useContext(RcmTableDataContext)
  const context = useServiceContext()
  const { translate } = context.service.filter
  const [selectedFolderType, setSelectedFolderType] = useState<FolderType | undefined>(undefined)
  const [showEditModal, setShowEditModal] = useState(false)
  const [isExporting, setIsExporting] = useState(false)

  const handleOpenEditModal = (folderType?: FolderType) => {
    setSelectedFolderType(folderType)
    setShowEditModal(true)
  }

  const handleCloseModal = () => {
    setShowEditModal(false)
    setSelectedFolderType(undefined)
  }
  return (
    <>
      <Loader blocking={isExporting}>
        <div className="row">
          <div className="col-xs-12">
            <div className="d-flex flex-wrap" style={{ paddingBottom: '1%' }}>
              <h1 className="align-left align-top" id="f038bab3-1878-4603-a8c0-1327f666d909" style={{ margin: '0' }}>
                {translate('FOLDER_TYPES_TITLE')}
              </h1>

              <div className="align-right align-bottom d-flex">
                <PageMenu
                  onAdd={() => handleOpenEditModal()}
                  onExportStart={() => setIsExporting(true)}
                  onExportEnd={() => setIsExporting(false)}
                />
                <RcmTableFilterTrigger />
              </div>
            </div>

            <TableFilter />
            <Table
              onRowClick={handleOpenEditModal}
              onDeleteSuccess={() => tableContext?.refreshTable()}
              onExportStart={() => setIsExporting(true)}
              onExportEnd={() => setIsExporting(false)}
            />
          </div>
        </div>
        {showEditModal && <ModalEdit folderType={selectedFolderType} onClose={handleCloseModal} />}
      </Loader>
    </>
  )
}

const FolderTypesPage: React.FC = () => {
  // Create loadData function for RcmTableDataWrapper
  const loadData = useCallback(async (pagingParams: any, filter: any) => {
    try {
      // Extract IDs from filter.types array - each item has structure { value: id, label: name }
      const typeIds = filter?.types && filter.types.length > 0 ? filter.types.map((t: any) => t.value || t.id || t) : []

      const params = {
        pageStart: pagingParams.pageStart || 0,
        pageSize: pagingParams.pageSize || 15,
        sortBy: pagingParams.sortBy || 'NAME',
        sortDesc: pagingParams.sortDesc !== undefined ? pagingParams.sortDesc : false,
        ...(typeIds.length > 0 ? { id: typeIds } : {})
      }

      const result: any = await folderTypesApiService.getAllForTable(params)

      return {
        elements: result.elements || [],
        totalElements: result.totalElements || 0
      }
    } catch (error) {
      console.error('Error loading folder types:', error)
      return {
        elements: [],
        totalElements: 0
      }
    }
  }, [])

  return (
    <div className="mcc-content-frame theme-sandman">
      <RcmTableDataWrapper
        loadData={loadData}
        defaultSortBy="NAME"
        defaultSortDesc={false}
        persistenceKey="settingsFolderTypes"
        doPreload={true}
      >
        <FolderTypesContent />
      </RcmTableDataWrapper>
    </div>
  )
}

export default FolderTypesPage
